import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { AGENT_KEYS } from '../features/agents/hooks/useAgents';
import { useSSEStream } from './useSSEStream';

const DEBOUNCE_MS = 400;

function parseLeadId(data: string): string | null {
  try {
    const parsed = JSON.parse(data);
    return typeof parsed?.leadId === 'string' ? parsed.leadId : null;
  } catch {
    return null;
  }
}

interface UseBoardRealtimeOptions {
  organizationId: string | undefined;
  enabled?: boolean;
  getAccessToken?: () => Promise<string | null>;
}

export function useBoardRealtime({
  organizationId,
  enabled = true,
  getAccessToken,
}: UseBoardRealtimeOptions) {
  const queryClient = useQueryClient();
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingLeadIdsRef = useRef<Set<string>>(new Set());

  const handleMessage = useCallback(
    (data: string) => {
      const leadId = parseLeadId(data);
      if (leadId) pendingLeadIdsRef.current.add(leadId);

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['board'] });
        queryClient.invalidateQueries({ queryKey: ['leads'] });
        if (pendingLeadIdsRef.current.size > 0) {
          for (const leadId of pendingLeadIdsRef.current) {
            queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
            queryClient.invalidateQueries({ queryKey: ['lead-activities', leadId] });
            queryClient.invalidateQueries({
              queryKey: [...AGENT_KEYS.all, 'lead-status', leadId],
            });
          }
          queryClient.invalidateQueries({ queryKey: ['leadStats'] });
          pendingLeadIdsRef.current.clear();
        }
        debounceTimerRef.current = null;
      }, DEBOUNCE_MS);
    },
    [queryClient],
  );

  const { isConnected } = useSSEStream({
    path: 'realtime/board/stream',
    organizationId,
    enabled,
    getAccessToken,
    onMessage: handleMessage,
  });

  const wasDroppedRef = useRef(false);
  const hasConnectedRef = useRef(false);

  useEffect(() => {
    if (!isConnected) {
      wasDroppedRef.current = hasConnectedRef.current;
      return;
    }
    hasConnectedRef.current = true;
    if (!wasDroppedRef.current) return;
    wasDroppedRef.current = false;
    for (const queryKey of [
      ['board'],
      ['leads'],
      ['lead'],
      ['leadStats'],
      [...AGENT_KEYS.all, 'lead-status'],
    ]) {
      queryClient.invalidateQueries({ queryKey });
    }
  }, [isConnected, queryClient]);

  return { isConnected };
}
